<template>
  <li v-if="itemData && itemData.owner">
    <div :id="`feedback_${itemData.id}`" :class="onlyComment" class="comment-body">
      <div :class="tagClass" class="avatar-profile" @click.stop="directReport">
        <img
          :src="itemData.owner.avatarUrl"
          :onerror="isNightMode ? avatarDefaultDark : avatarDefault"
          class="avatar"
        >
      </div>

      <div class="comment-wrap">
        <div :class="commentAreaSpacing ? 'all-comment-area' : 'comment-area'">
          <div class="comment-top-info">
            <UserProfile :item-data="itemData" />

            <PraiseButton
              :comment-id="itemData.id"
              :thumbup-count="itemData.thumbupCount"
              :is-thumb-up="itemData.isThumbUp"
              :comment-owner="itemData.owner"
            />
          </div>

          <ContentBody
            type="comment"
            :data="itemData"
            @click.stop="clickCommentContent(itemData)"
          />

          <MenuBar :type="type" :item-data="itemData" />
        </div>
      </div>
    </div>
    <ReplyList
      :comment-id="itemData.id"
      :comment-owner="itemData.owner"
      :replies="itemData.replies"
      :reply-size="itemData.replySize"
      @click.stop="onCommentClickThrottle(itemData)"
    />
  </li>
</template>

<script lang="ts">
import { utils, constant } from '@cpc/comment-sdk-common';
import { useGlobalState, useStat } from '@cpc/comment-sdk-core-comment';
import { defineComponent, defineAsyncComponent, toRefs, computed, onMounted } from 'vue';

import avatarDefault from '../assets/img/avatar_default.png';
import avatarDefaultDark from '../assets/img/avatar_default_dark.png';
import { useOnCommentClickInject } from '../core/useOnCommentClickProvide/index';
import { commonCommentStat } from '../core/useStat';

export default defineComponent({
  name: 'CommentItem',

  components: {
    PraiseButton: defineAsyncComponent(() => import('./PraiseButton.vue')),
    ReplyList: defineAsyncComponent(() => import('./ReplyList.vue')),
    ContentBody: defineAsyncComponent(() => import('./ContentBody.vue')),
    MenuBar: defineAsyncComponent(() => import('./MenuBar.vue')),
    UserProfile: defineAsyncComponent(() => import('./UserProfile.vue')),
  },

  props: {
    itemData: {
      type: Object,
      default() {
        return {};
      },
    },
    type: {
      type: String,
      default: 'new',
    },
    index: {
      type: Number,
      default: -1,
    },
  },

  setup(props, { emit }) {
    const { PageType } = constant;
    const { translateTimeStamp, $ } = utils;
    const { isNightMode, documentMeta, pageType } = toRefs(useGlobalState());

    const { onCommentClickThrottle } = useOnCommentClickInject();

    const commentAreaSpacing = computed(() => {
      return documentMeta.value.styleType === 1 && pageType.value === PageType.COMMENT_DETAIL;
    });

    const { userMeta } = toRefs(useGlobalState());
    const { itemData } = toRefs(props);
    const directReport = () => {
      // 点击评论用户头像埋点
      commonCommentStat(20084690, {
        userId: itemData.value.owner.id,
      });

      if (userMeta.value.uid === itemData.value.owner.id) {
        return;
      }

      onCommentClickThrottle(itemData.value, {
        directReport: true,
      });
    };

    const onlyComment = computed(() => {
      return itemData.value.replies && itemData.value.replies.length > 0 ? '' : 'only-comment';
    });

    const tagClass = computed(() => {
      return {
        'tag': true,
        'tag-good': itemData.value.tag === 'GOOD',
        'tag-god': itemData.value.tag === 'GOD',
        'tag-hot': itemData.value.tag === 'HOT',
      };
    });

    const clickCommentContent = (itemData) => {
      commonCommentStat(20084688);

      onCommentClickThrottle(itemData, {
        directComment: true,
      });
    };

    onMounted(() => {
      if (props.type === 'new' && props.index === 1) {
        const durationTime = window.__TEMPLATE_COMMEMT_DATA__?.startTime
          ? Date.now() - window.__TEMPLATE_COMMEMT_DATA__.startTime
          : window.performance.now();

        window.generalPerfStat?.({ action: 'firstComment', durationTime });
        window.commentCommonHelps?.performanceStat?.('H5_commentViewsRenderComplete');
      }
    });

    return {
      translateTimeStamp,
      isNightMode,
      commentAreaSpacing,

      onlyComment,
      tagClass,

      onCommentClickThrottle,
      directReport,

      avatarDefault,
      avatarDefaultDark,
      clickCommentContent,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../assets/css/styling-mixin.scss';

.comment-item {
  color: #000;
}

.comment-body {
  display: flex;
  padding: 24px 48px 0;
}

// .only-comment {
//   padding-bottom: 30px;
// }

.avatar-profile {
  width: 108px;
  height: 108px;
  border-radius: 54px;
  position: relative;
  &:active {
    transform: scale(0.8);
    transition: all 0.3s ease;
  }

  .avatar {
    width: 96px;
    height: 96px;
    border-radius: 54px;
    // margin: 6px;
  }
}

.tag {
  width: 108px;
  height: 108px;
  border-radius: 54px;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    background-repeat: no-repeat;
    background-size: contain;
    border-radius: 54px;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  &-hot,
  &-good,
  &-god {
    .avatar {
      margin: 6px;
    }
  }

  &-hot {
    &::after {
      background-image: url('../assets/img/hot-circle.png');
    }
  }

  &-good {
    &::after {
      background-image: url('../assets/img/good-circle.png');
    }
  }
  &-god {
    &::after {
      background-image: url('../assets/img/god-circle.png');
    }
  }
}

.comment-wrap {
  margin-left: 24px;
  font-size: 36px;
  font-family: ColorFont-Regular, 'OPlusSans 3.0';
  flex: 1;
  min-width: 0; // fix flex & ellipsis
  .all-comment-area {
    @include os-browser-styling(13, 'not-video') {
      margin: 12px 0;
    }
  }

  .comment-top-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 48px;
    padding: 18px 0 12px;
  }
}

html[data-theme='dark'] {
  .comment-content {
    color: rgba(255, 255, 255, 0.7);
  }
}
</style>
